const DIARY_SUPABASE_URL = 'https://pnqdzbseudwoujywuzxm.supabase.co';
const DIARY_SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBucWR6YnNldWR3b3VqeXd1enhtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njg3ODExMTEsImV4cCI6MjA4NDM1NzExMX0.TJADgwQpjSaQYOx_g9IuopVSV7O5LZmlpVo5_-ulQoI';

declare const supabase: any;

export class DiaryWidget extends HTMLElement {
    private diarySb: any;
    private mode: string | null = null;
    private date: string | null = null;
    private draftContent: string | null = null;
    private mood: string | null = null;

    private container!: HTMLDivElement;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        
        // Initialize Diary Supabase client
        if (typeof supabase !== 'undefined') {
            this.diarySb = supabase.createClient(DIARY_SUPABASE_URL, DIARY_SUPABASE_KEY);
        }
        
        this.renderBase();
    }

    static get observedAttributes() {
        return ['mode', 'date', 'draft', 'mood'];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (newValue) {
            if (name === 'mode') this.mode = newValue;
            if (name === 'date') this.date = newValue;
            if (name === 'draft') this.draftContent = decodeURIComponent(newValue);
            if (name === 'mood') this.mood = newValue;
            
            this.executeAction();
        }
    }

    renderBase() {
        this.shadowRoot!.innerHTML = `
            <style>
                :host {
                    display: block;
                    width: 100%;
                    max-width: 500px;
                    margin: 15px 0;
                    font-family: var(--font-family, system-ui, sans-serif);
                }
                .widget-card {
                    background: #2D1810; /* Cozy Warmth Theme from Diary */
                    border: 1px solid #D4835C;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.3);
                }
                .header {
                    background: #4A2F1F;
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: #fff;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                }
                .logo {
                    width: 24px;
                    height: 24px;
                    background: #D4835C;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: bold;
                    color: #fff;
                }
                .title {
                    font-weight: 600;
                    font-size: 14px;
                }
                .body {
                    padding: 16px;
                    color: #f3f4f6;
                }
                .content-box {
                    background: rgba(0,0,0,0.2);
                    padding: 12px;
                    border-radius: 8px;
                    font-size: 13px;
                    line-height: 1.5;
                    margin-bottom: 15px;
                    white-space: pre-wrap;
                }
                .btn {
                    background: #D4835C;
                    color: #fff;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    width: 100%;
                    transition: opacity 0.2s;
                }
                .btn:hover {
                    opacity: 0.9;
                }
                .mood-badge {
                    display: inline-block;
                    background: rgba(255,255,255,0.1);
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                    margin-bottom: 10px;
                }
                .login-warning {
                    font-size: 12px;
                    color: #fbbf24;
                    margin-top: 10px;
                    text-align: center;
                }
            </style>
            <div class="widget-card">
                <div class="header">
                    <div class="logo"><i class="fas fa-book"></i></div>
                    <div class="title" id="widgetTitle">My Diary Assistant</div>
                </div>
                <div class="body" id="widgetBody">
                    Loading diary context...
                </div>
            </div>
        `;
        this.container = this.shadowRoot!.getElementById('widgetBody') as HTMLDivElement;
    }

    async executeAction() {
        if (!this.mode) return;

        // Check auth status
        const { data: { session } } = await this.diarySb.auth.getSession();
        
        if (this.mode === 'draft') {
            this.renderDraftView(session);
        } else if (this.mode === 'memory') {
            this.renderMemoryView(session);
        }
    }

    renderDraftView(session: any) {
        const title = this.shadowRoot!.getElementById('widgetTitle')!;
        title.textContent = 'Drafted Diary Entry';

        let html = '';
        if (this.date) {
            html += `<div class="mood-badge">📅 ${this.date}</div>`;
        }
        if (this.mood) {
            html += ` <div class="mood-badge">Mood: ${this.mood}</div>`;
        }

        html += `
            <div class="content-box">${this.draftContent || 'No content provided.'}</div>
        `;

        if (session) {
            html += `<button class="btn" id="saveBtn">Save to My Diary</button>`;
        } else {
            html += `
                <button class="btn" onclick="window.open('https://diary.9rk.pages.dev', '_blank')">Open Diary App</button>
                <div class="login-warning">Log into My Diary to save entries automatically.</div>
            `;
        }

        this.container.innerHTML = html;

        const saveBtn = this.shadowRoot!.getElementById('saveBtn');
        if (saveBtn) {
            saveBtn.addEventListener('click', () => this.saveToDiary(session));
        }
    }

    async renderMemoryView(session: any) {
        const title = this.shadowRoot!.getElementById('widgetTitle')!;
        title.textContent = `Diary Memories ${this.date ? '(' + this.date + ')' : ''}`;

        if (!session) {
            this.container.innerHTML = `
                <div style="text-align:center; padding: 20px;">
                    <p>I need access to your diary to retrieve memories.</p>
                    <button class="btn" onclick="window.open('https://diary.9rk.pages.dev', '_blank')">Login to My Diary</button>
                </div>
            `;
            return;
        }

        this.container.innerHTML = '<i>Searching diary archives...</i>';

        try {
            let query = this.diarySb.from('diary_entries').select('*').eq('user_id', session.user.id);
            
            if (this.date) {
                // simple date matching
                query = query.like('created_at', `${this.date}%`);
            }
            
            query = query.order('created_at', { ascending: false }).limit(3);

            const { data, error } = await query;

            if (error) throw error;

            if (data && data.length > 0) {
                let html = '<div style="display:flex; flex-direction:column; gap:10px;">';
                data.forEach((entry: any) => {
                    const dateStr = new Date(entry.created_at).toLocaleDateString();
                    html += `
                        <div class="content-box" style="margin:0;">
                            <strong style="color:#D4835C;">${dateStr} ${entry.mood ? '- ' + entry.mood : ''}</strong><br/>
                            ${entry.content ? entry.content.substring(0, 150) + '...' : 'No text content.'}
                        </div>
                    `;
                });
                html += '</div>';
                this.container.innerHTML = html;
            } else {
                this.container.innerHTML = 'No entries found for this time period.';
            }
        } catch (e: any) {
            this.container.innerHTML = `<div style="color:#ef4444;">Error fetching memories: ${e.message}</div>`;
        }
    }

    async saveToDiary(session: any) {
        const btn = this.shadowRoot!.getElementById('saveBtn') as HTMLButtonElement;
        btn.textContent = 'Saving...';
        btn.disabled = true;

        try {
            const { error } = await this.diarySb.from('diary_entries').insert({
                id: crypto.randomUUID(),
                user_id: session.user.id,
                content: this.draftContent || '',
                mood: this.mood || 'neutral',
                created_at: new Date().toISOString(),
                status: 'published'
            });

            if (error) throw error;

            btn.textContent = 'Saved Successfully!';
            btn.style.background = '#10B981';
        } catch (e: any) {
            btn.textContent = 'Error Saving';
            btn.style.background = '#EF4444';
            console.error(e);
        }
    }
}

export function initDiaryBridge() {
    if (!customElements.get('diary-widget')) {
        customElements.define('diary-widget', DiaryWidget);
    }
}
